import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Activity, Pause, Play } from "lucide-react";

export function InsightsTab() {
  const [paused, setPaused] = useState<boolean | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data, error } = await (supabase as any)
      .from("insights_config")
      .select("events_paused, updated_at")
      .eq("id", 1)
      .maybeSingle();
    if (error) {
      toast.error("Kunde inte hämta insights-konfiguration");
      return;
    }
    setPaused(!!data?.events_paused);
    setUpdatedAt(data?.updated_at || null);
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async () => {
    if (paused === null) return;
    setSaving(true);
    const next = !paused;
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await (supabase as any)
      .from("insights_config")
      .update({
        events_paused: next,
        updated_at: new Date().toISOString(),
        updated_by: auth?.user?.id ?? null,
      })
      .eq("id", 1);
    setSaving(false);
    if (error) {
      toast.error("Kunde inte uppdatera");
      return;
    }
    setPaused(next);
    setUpdatedAt(new Date().toISOString());
    toast.success(next ? "Insights pausad" : "Insights återupptagen");
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border bg-card p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <Activity className="h-5 w-5" /> Listing events-loggning
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Pausa eller återuppta insamling av interaktionsdata för annonser
              (visningar, klick, meddelanden, köpförslag). Användbart för att
              spara databaslagring när insikter inte behövs.
            </p>
          </div>
          {paused !== null && (
            <Badge variant={paused ? "secondary" : "default"}>
              {paused ? "Pausad" : "Aktiv"}
            </Badge>
          )}
        </div>

        {paused === null ? (
          <p className="text-sm text-muted-foreground">Laddar…</p>
        ) : (
          <div className="flex items-center gap-3">
            <Button onClick={toggle} disabled={saving} className="gap-2">
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              {paused ? "Återuppta loggning" : "Pausa loggning"}
            </Button>
            {updatedAt && (
              <span className="text-xs text-muted-foreground">
                Senast ändrad{" "}
                {new Date(updatedAt).toLocaleString("sv-SE", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Fler insikter och statistik kommer här i framtiden.
        </p>
      </div>
    </div>
  );
}
